import * as THREE from 'three';
import { useGalaxyStore } from '../../core/state/GalaxyStore';
import { SelectionManager } from '../managers/SelectionManager';
import { FeedbackManager } from '../managers/FeedbackManager';

export class MouseController {
  private canvas: HTMLCanvasElement;
  private selectionManager: SelectionManager;
  private feedbackManager: FeedbackManager;
  private store: typeof useGalaxyStore;
  
  private isEnabled: boolean = false;
  private isDragging: boolean = false;
  private dragStartTime: number = 0;
  private dragThreshold: number = 5; // pixels
  private clickThreshold: number = 200; // ms
  
  // Event listeners (stored for cleanup)
  private boundEventListeners: Map<string, EventListener> = new Map();
  
  constructor(
    canvas: HTMLCanvasElement,
    selectionManager: SelectionManager,
    feedbackManager: FeedbackManager,
    store: typeof useGalaxyStore
  ) {
    this.canvas = canvas;
    this.selectionManager = selectionManager;
    this.feedbackManager = feedbackManager;
    this.store = store;
    
    this.setupEventListeners();
  }
  
  private setupEventListeners(): void {
    // Mouse move for hover detection
    const onMouseMove = (event: MouseEvent) => {
      if (!this.isEnabled) return;
      
      this.selectionManager.updatePointer(event.clientX, event.clientY, this.canvas);
      const hoveredObject = this.selectionManager.updateHover();
      
      // Update cursor based on hover state
      this.updateCursor(hoveredObject);
    };
    
    // Mouse down to start potential drag or click
    const onMouseDown = (event: MouseEvent) => {
      if (!this.isEnabled || event.button !== 0) return; // Only left mouse button
      
      this.isDragging = false;
      this.dragStartTime = Date.now();
      
      // Update pointer position
      this.selectionManager.updatePointer(event.clientX, event.clientY, this.canvas);
    };
    
    // Mouse up for click detection
    const onMouseUp = (event: MouseEvent) => {
      if (!this.isEnabled || event.button !== 0) return;
      
      const clickDuration = Date.now() - this.dragStartTime;
      
      // If it was a quick click (not a drag), handle selection
      if (!this.isDragging && clickDuration < this.clickThreshold) {
        this.handleClick(event);
      }
      
      this.isDragging = false;
    };
    
    // Context menu (right click) - prevent default for now
    const onContextMenu = (event: MouseEvent) => {
      if (!this.isEnabled) return;
      event.preventDefault();
      // Could add context menu functionality here later
    };
    
    // Mouse leave to clear hover
    const onMouseLeave = () => {
      if (!this.isEnabled) return;
      this.selectionManager.clearHover();
      this.updateCursor(null);
    };
    
    // Store bound listeners for cleanup
    this.boundEventListeners.set('mousemove', onMouseMove);
    this.boundEventListeners.set('mousedown', onMouseDown);
    this.boundEventListeners.set('mouseup', onMouseUp);
    this.boundEventListeners.set('contextmenu', onContextMenu);
    this.boundEventListeners.set('mouseleave', onMouseLeave);
  }
  
  private handleClick(event: MouseEvent): void {
    // Update pointer position for accurate raycast
    this.selectionManager.updatePointer(event.clientX, event.clientY, this.canvas);
    
    // Get clicked object
    const clickedObject = this.selectionManager.getClickTarget();
    
    if (clickedObject) {
      console.log('Object clicked:', clickedObject.userData);
      
      // Update selection in store
      const objectId = clickedObject.userData['id'];
      if (objectId) {
        this.store.getState().selectObject(objectId);
        
        // Provide visual feedback
        this.feedbackManager.triggerClickFeedback(clickedObject);
        
        // Log selection for debugging
        console.log('Selected object:', {
          id: objectId,
          type: clickedObject.userData['type'],
          position: clickedObject.position,
        });
      }
    } else {
      // Clicked on empty space - clear selection
      this.store.getState().selectObject(null);
      console.log('Selection cleared - clicked on empty space');
    }
  }
  
  private updateCursor(hoveredObject: THREE.Object3D | null): void {
    if (hoveredObject) {
      this.canvas.style.cursor = 'pointer';
    } else {
      this.canvas.style.cursor = 'default';
    }
  }
  
  public enable(): void {
    if (this.isEnabled) return;
    
    this.isEnabled = true;
    
    // Add event listeners
    this.boundEventListeners.forEach((listener, event) => {
      this.canvas.addEventListener(event, listener);
    });
    
    console.log('MouseController enabled');
  }
  
  public disable(): void {
    if (!this.isEnabled) return;
    
    this.isEnabled = false;
    
    // Remove event listeners
    this.boundEventListeners.forEach((listener, event) => {
      this.canvas.removeEventListener(event, listener);
    });
    
    // Clear any hover state
    this.selectionManager.clearHover();
    this.updateCursor(null);
    
    console.log('MouseController disabled');
  }
  
  public dispose(): void {
    this.disable();
    this.boundEventListeners.clear();
  }
}